import api from './client';

// Limits API - BDSM activity limits tracker
export const limitsAPI = {
  // Get all categories
  getCategories: () => api.get('/limits/categories'),

  // Get activities (optionally by category)
  getActivities: (categoryId?: number) =>
    api.get('/limits/activities', { params: categoryId ? { categoryId } : undefined }),

  // Get user's full checklist with limits
  getChecklist: () => api.get('/limits/checklist'),

  // Get user's limits summary
  getSummary: () => api.get('/limits/summary'),

  // Spec 312 — Negotiation Copilot pre-negotiation nudge. Self-scoped: the
  // caller's own unrated, requires_discussion=true checklist activities.
  // Feature-flag gated ('negotiation_copilot') server-side.
  getUnratedFlaggedChecklist: () => api.get('/limits/checklist/unrated-flagged'),

  // Update single activity limit. activityId is the RAW id — either a bare
  // number (system activity) or "custom_<n>" (custom activity) — do NOT
  // strip the "custom_" prefix before calling this; the backend parses it
  // (bug-registry.md data-corruption bug: stripping it here is exactly how
  // a custom activity's id used to collide with an unrelated system
  // activity's id).
  updateLimit: (activityId: number | string, data: { experience?: string; interest?: string; notes?: string }) =>
    api.put(`/limits/activity/${activityId}`, data),

  // Bulk update limits. Each activityId follows the same raw-id convention
  // as updateLimit above (may be "custom_<n>").
  bulkUpdateLimits: (limits: { activityId: number | string; experience?: string; interest?: string }[]) =>
    api.post('/limits/bulk-update', { limits }),

  // Get compatibility analysis
  getCompatibility: (dominantId: string | number, submissiveId: string | number) =>
    api.get('/limits/compatibility', { params: { dominantId, submissiveId } }),

  // Get the caller's actual linked Dominant/submissive partner(s) in this org
  // (active keyholder assignment or partner relationship) — used to auto-select
  // (or offer a picker for) the Compatibility tab's partner, instead of
  // guessing "the first other org member" (docs/reference/bug-registry.md,
  // GAP filed 2026-08-02).
  getLinkedPartners: () => api.get('/limits/linked-partners'),

  // Add custom activity
  addCustomActivity: (data: { categoryId?: number; name: string; description?: string }) =>
    api.post('/limits/custom', data),

  // Get custom activities
  getCustomActivities: () => api.get('/limits/custom'),

  // Update custom activity
  updateCustomActivity: (
    activityId: number,
    data: { categoryId?: number | null; name?: string; description?: string | null }
  ) => api.put(`/limits/custom/${activityId}`, data),

  // Delete custom activity
  deleteCustomActivity: (activityId: number) => api.delete(`/limits/custom/${activityId}`),

  // Get limit history
  getHistory: (params?: { userId?: number; activityId?: number; limit?: number }) =>
    api.get('/limits/history', { params }),

  // === Negotiation ===

  // Start a new negotiation session with a specific partner
  startNegotiation: (partnerId: string) =>
    api.post('/limits/negotiations', { partnerId }),

  // Get active negotiations (optionally filtered by partner)
  getActiveNegotiation: (partnerId?: string) =>
    api.get('/limits/negotiations/active', { params: partnerId ? { partnerId } : undefined }),

  // Get negotiation history (all statuses)
  getNegotiationHistory: () => api.get('/limits/negotiations/history'),

  // Mark my limits as ready/submitted
  submitNegotiation: (negotiationId: number) =>
    api.post(`/limits/negotiations/${negotiationId}/submit`),

  // Reveal overlap (both must have submitted)
  revealNegotiation: (negotiationId: number) =>
    api.post(`/limits/negotiations/${negotiationId}/reveal`),

  // Get overlap/diff result
  getNegotiationResult: (negotiationId: number) =>
    api.get(`/limits/negotiations/${negotiationId}/result`),

  // Spec 312 — Negotiation Copilot. 3 derived "worth discussing" lists for a
  // revealed negotiation (unratedFlagged, undiscussedFlagged,
  // undiscussedSafetyNotes). 409 if not yet revealed, 404 if caller isn't a
  // party -- both should degrade silently on the frontend (additive-only:
  // never blocks the existing classification list below it). Feature-flag
  // gated ('negotiation_copilot') server-side.
  getNegotiationCopilot: (negotiationId: number) =>
    api.get(`/limits/negotiations/${negotiationId}/copilot`),

  // Cancel an unrevealed negotiation (either party). Added for Spec 271 —
  // not wired to any UI in the training-program task cards themselves (the
  // mockups for those states show no cancel affordance); this exists so a
  // cancel entry point can be added to the main negotiation UI (e.g.
  // NegotiationsTab) without a further backend round-trip.
  cancelNegotiation: (negotiationId: number) =>
    api.post(`/limits/negotiations/${negotiationId}/cancel`),

  // Accept/decline a pending negotiation invitation (Spec 303 Fix 2). Only
  // the invited partner may call these — the initiator gets a 404.
  acceptNegotiation: (negotiationId: number) =>
    api.post(`/limits/negotiations/${negotiationId}/accept`),
  declineNegotiation: (negotiationId: number) =>
    api.post(`/limits/negotiations/${negotiationId}/decline`),

  // === Discussion threads ===

  // negotiationId is required (bug-registry 0zzz-af) — a note must be
  // attributed to a specific pair's negotiation, not just the activity, or
  // it silently pools with every other pair's notes on the same activity.
  addDiscussion: (activityId: number, note: string, negotiationId: number) =>
    api.post(`/limits/activities/${activityId}/discussions`, { note, negotiationId }),

  getDiscussions: (activityId: number, negotiationId: number) =>
    api.get(`/limits/activities/${activityId}/discussions`, { params: { negotiationId } }),

  // === Snapshots ===

  getSnapshots: () => api.get('/limits/snapshots'),

  getSnapshotDiff: (snapshotId1: number, snapshotId2: number) =>
    api.get(`/limits/snapshots/${snapshotId1}/diff/${snapshotId2}`),

  // === Scene Composer (Spec 313) ===

  // Composer catalog — same dominantId/submissiveId query-param convention as
  // getCompatibility() above (reads the exact same live user_limits data, so
  // it 409s identically while a negotiation between the pair is pending
  // reveal). Feature-flag gated ('scene_composer') server-side; on any error
  // the composer catalog panel must fail silently — the rest of the scene
  // plan page (title/date/notes CRUD) is unaffected.
  getComposerCatalog: (dominantId: string, submissiveId: string) =>
    api.get('/limits/compatibility/composer', { params: { dominantId, submissiveId } }),

  // Discuss-flow state for one specific partner — drives a "needs discussion"
  // card's Discuss action (revealed -> deep-link, pending -> "reveal to
  // discuss" messaging, none -> "start a negotiation"). Feature-flag gated
  // ('scene_composer') server-side.
  getComposerDiscussState: (partnerId: string) =>
    api.get('/limits/negotiations/discuss-state', { params: { partnerId } }),
};
